/**
 * FDACredibilityCard - FDA AI Credibility Dashboard Widget
 * ============================================================================
 * DC-930: Dashboard widget displaying FDA AI Credibility assessment results
 * with Model Risk context visualization.
 *
 * Features:
 * - Overall credibility score gauge
 * - Credibility level badge (high/adequate/low/insufficient)
 * - Dimension scores (relevance, reliability)
 * - Model risk section (influence, consequence, calculated risk)
 * - Gaps summary by severity
 * - Actions: refresh, run assessment, export (DC-938)
 *
 * Pattern: Follows AlcoaSummaryCard.tsx (DC-513), ComplianceLensCard.tsx (DC-821)
 *
 * @version 1.0.0
 * @created 2026-01-10
 * ============================================================================
 */

import React, { useCallback, useState } from 'react';
import { ScoreGauge } from '../vendor/ScoreGauge';
import { ExportCredibilityReport } from '../fda/ExportCredibilityReport';
import {
  useFDACredibility,
  getCredibilityLevelColor,
  getCredibilityLevelDescription,
  getRiskLevelColor,
  getSeverityColor,
  type FDACredibilityLevel,
  type CalculatedRiskLevel,
  type ModelInfluence,
  type DecisionConsequence,
} from './useFDACredibility';
import { RegulatoryBasisDrawer } from '../evidence/RegulatoryBasisDrawer';
import type { EducationalMetric } from '../evidence/RegulatoryBasisDrawer';
import type { RegulatoryRequirement } from '../../types/evidence';
import './fda-credibility-card.css';

// ============================================================================
// Types
// ============================================================================

export interface FDACredibilityCardProps {
  /** Journey ID to fetch assessment for */
  journeyId: string;
  /** Compact mode for smaller spaces */
  compact?: boolean;
  /** Show actions (refresh, run assessment) */
  showActions?: boolean;
  /** Callback when "View Details" is clicked */
  onViewDetails?: () => void;
  /** Callback after assessment completes */
  onAssessmentComplete?: () => void;
  /** Custom className */
  className?: string;
}

// ============================================================================
// Helper Components
// ============================================================================

interface CredibilityBadgeProps {
  level: FDACredibilityLevel;
  compact?: boolean;
}

const CredibilityBadge: React.FC<CredibilityBadgeProps> = ({ level, compact }) => {
  const color = getCredibilityLevelColor(level);
  const labels: Record<FDACredibilityLevel, string> = {
    high: 'High Credibility',
    adequate: 'Adequate',
    low: 'Low Credibility',
    insufficient: 'Insufficient',
  };

  return (
    <span
      className={`fda-credibility-badge fda-credibility-badge--${level} ${compact ? 'fda-credibility-badge--compact' : ''}`}
      style={{ backgroundColor: `${color}15`, color, borderColor: color }}
    >
      {compact ? level.charAt(0).toUpperCase() + level.slice(1) : labels[level]}
    </span>
  );
};

interface FitForUseBadgeProps {
  isFitForUse: boolean;
}

const FitForUseBadge: React.FC<FitForUseBadgeProps> = ({ isFitForUse }) => {
  return (
    <span className={`fda-fit-badge fda-fit-badge--${isFitForUse ? 'yes' : 'no'}`}>
      {isFitForUse ? 'Fit for Use' : 'Not Fit for Use'}
    </span>
  );
};

interface DimensionScoreProps {
  label: string;
  score: number | undefined;
  maxScore?: number;
  /** DC-1065: Callback to view regulatory basis for this dimension */
  onViewBasis?: () => void;
}

const DimensionScore: React.FC<DimensionScoreProps> = ({ label, score, maxScore = 100, onViewBasis }) => {
  if (score === undefined) return null;

  const percentage = Math.round((score / maxScore) * 100);
  const getBarColor = (pct: number): string => {
    if (pct >= 85) return 'var(--tri-good)';
    if (pct >= 70) return 'var(--tri-soft)';
    if (pct >= 50) return 'var(--tri-terra)';
    return 'var(--tri-hard)';
  };

  return (
    <div className="fda-dimension-score">
      <div className="fda-dimension-score__header">
        <span className="fda-dimension-score__label">{label}</span>
        <div className="fda-dimension-score__header-right">
          <span className="fda-dimension-score__value">{Math.round(score)}</span>
          {onViewBasis && (
            <button
              type="button"
              className="fda-basis-btn"
              onClick={onViewBasis}
              title={`View regulatory basis for ${label}`}
              aria-label={`View regulatory basis for ${label}`}
            >
              ?
            </button>
          )}
        </div>
      </div>
      <div className="fda-dimension-score__bar-bg">
        <div
          className="fda-dimension-score__bar-fill"
          style={{
            width: `${percentage}%`,
            backgroundColor: getBarColor(percentage),
          }}
        />
      </div>
    </div>
  );
};

interface ModelRiskDisplayProps {
  influence: ModelInfluence;
  consequence: DecisionConsequence;
  calculatedRisk: CalculatedRiskLevel;
}

const ModelRiskDisplay: React.FC<ModelRiskDisplayProps> = ({
  influence,
  consequence,
  calculatedRisk,
}) => {
  const riskColor = getRiskLevelColor(calculatedRisk);

  const influenceLabels: Record<ModelInfluence, string> = {
    low: 'Low',
    medium: 'Medium',
    high: 'High',
  };

  const consequenceLabels: Record<DecisionConsequence, string> = {
    low: 'Low',
    medium: 'Medium',
    high: 'High',
    critical: 'Critical',
  };

  return (
    <div className="fda-model-risk">
      <div className="fda-model-risk__header">
        <span className="fda-model-risk__title">Model Risk</span>
        <span
          className="fda-model-risk__level"
          style={{ backgroundColor: `${riskColor}15`, color: riskColor }}
        >
          {calculatedRisk.charAt(0).toUpperCase() + calculatedRisk.slice(1)} Risk
        </span>
      </div>
      <div className="fda-model-risk__grid">
        <div className="fda-model-risk__item">
          <span className="fda-model-risk__item-label">Influence</span>
          <span className="fda-model-risk__item-value">{influenceLabels[influence]}</span>
        </div>
        <div className="fda-model-risk__item">
          <span className="fda-model-risk__item-label">Consequence</span>
          <span className="fda-model-risk__item-value">{consequenceLabels[consequence]}</span>
        </div>
      </div>
    </div>
  );
};

interface GapsSummaryDisplayProps {
  critical: number;
  major: number;
  minor: number;
  observation: number;
  compact?: boolean;
}

const GapsSummaryDisplay: React.FC<GapsSummaryDisplayProps> = ({
  critical,
  major,
  minor,
  observation,
  compact,
}) => {
  const items = [
    { label: 'Critical', count: critical, severity: 'critical' as const },
    { label: 'Major', count: major, severity: 'major' as const },
    { label: 'Minor', count: minor, severity: 'minor' as const },
    { label: 'Obs', count: observation, severity: 'observation' as const },
  ];

  if (compact) {
    // Only show non-zero counts in compact mode
    const nonZero = items.filter((item) => item.count > 0);
    if (nonZero.length === 0) {
      return (
        <div className="fda-gaps-summary fda-gaps-summary--compact">
          <span className="fda-gaps-summary__none">No gaps found</span>
        </div>
      );
    }
    return (
      <div className="fda-gaps-summary fda-gaps-summary--compact">
        {nonZero.map((item) => (
          <span
            key={item.severity}
            className="fda-gaps-summary__badge"
            style={{
              backgroundColor: `${getSeverityColor(item.severity)}15`,
              color: getSeverityColor(item.severity),
            }}
          >
            {item.count} {item.label}
          </span>
        ))}
      </div>
    );
  }

  return (
    <div className="fda-gaps-summary">
      <div className="fda-gaps-summary__title">Gaps Found</div>
      <div className="fda-gaps-summary__grid">
        {items.map((item) => (
          <div key={item.severity} className="fda-gaps-summary__item">
            <span
              className="fda-gaps-summary__count"
              style={{ color: item.count > 0 ? getSeverityColor(item.severity) : 'var(--tri-slate)' }}
            >
              {item.count}
            </span>
            <span className="fda-gaps-summary__label">{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// Loading State
// ============================================================================

const LoadingState: React.FC<{ compact?: boolean }> = ({ compact }) => (
  <div className={`fda-credibility-card fda-credibility-card--loading ${compact ? 'fda-credibility-card--compact' : ''}`}>
    <div className="fda-credibility-card__header">
      <div className="fda-loading-skeleton fda-loading-skeleton--title" />
    </div>
    <div className="fda-credibility-card__body">
      <div className="fda-loading-skeleton fda-loading-skeleton--gauge" />
      <div className="fda-loading-skeleton fda-loading-skeleton--text" />
      <div className="fda-loading-skeleton fda-loading-skeleton--text" />
    </div>
  </div>
);

// ============================================================================
// Empty State
// ============================================================================

interface EmptyStateProps {
  onRunAssessment?: () => void;
  isRunning?: boolean;
  compact?: boolean;
}

const EmptyState: React.FC<EmptyStateProps> = ({ onRunAssessment, isRunning, compact }) => (
  <div className={`fda-credibility-card fda-credibility-card--empty ${compact ? 'fda-credibility-card--compact' : ''}`}>
    <div className="fda-credibility-card__header">
      <h3 className="fda-credibility-card__title">FDA AI Credibility</h3>
    </div>
    <div className="fda-credibility-card__body fda-credibility-card__body--empty">
      <div className="fda-empty-state">
        <svg
          className="fda-empty-state__icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z"
          />
        </svg>
        <p className="fda-empty-state__text">No FDA credibility assessment yet</p>
        {onRunAssessment && (
          <button
            className="fda-empty-state__button"
            onClick={onRunAssessment}
            disabled={isRunning}
          >
            {isRunning ? 'Running...' : 'Run Assessment'}
          </button>
        )}
      </div>
    </div>
  </div>
);

// ============================================================================
// Error State
// ============================================================================

interface ErrorStateProps {
  error: Error;
  onRetry?: () => void;
  compact?: boolean;
}

const ErrorState: React.FC<ErrorStateProps> = ({ error, onRetry, compact }) => (
  <div className={`fda-credibility-card fda-credibility-card--error ${compact ? 'fda-credibility-card--compact' : ''}`}>
    <div className="fda-credibility-card__header">
      <h3 className="fda-credibility-card__title">FDA AI Credibility</h3>
    </div>
    <div className="fda-credibility-card__body fda-credibility-card__body--error">
      <div className="fda-error-state">
        <svg
          className="fda-error-state__icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
          />
        </svg>
        <p className="fda-error-state__text">{error.message}</p>
        {onRetry && (
          <button className="fda-error-state__button" onClick={onRetry}>
            Retry
          </button>
        )}
      </div>
    </div>
  </div>
);

// ============================================================================
// Main Component
// ============================================================================

export const FDACredibilityCard: React.FC<FDACredibilityCardProps> = ({
  journeyId,
  compact = false,
  showActions = true,
  onViewDetails,
  onAssessmentComplete,
  className = '',
}) => {
  const { result, loading, error, isRunning, fetchResult, runAssessment, clearError } =
    useFDACredibility(journeyId);

  // DC-1065: State for RegulatoryBasisDrawer
  const [basisDrawerOpen, setBasisDrawerOpen] = useState(false);
  const [basisMetric, setBasisMetric] = useState<EducationalMetric | null>(null);
  const [basisRequirement, setBasisRequirement] = useState<RegulatoryRequirement | null>(null);
  const [basisLoading, setBasisLoading] = useState(false);
  const [basisError, setBasisError] = useState<string | null>(null);

  const handleRunAssessment = useCallback(async () => {
    const newResult = await runAssessment();
    if (newResult && onAssessmentComplete) {
      onAssessmentComplete();
    }
  }, [runAssessment, onAssessmentComplete]);

  const handleRefresh = useCallback(() => {
    clearError();
    fetchResult();
  }, [clearError, fetchResult]);

  // DC-1065: Handler to view regulatory basis for a dimension
  const handleViewBasis = useCallback(async (dimensionName: string) => {
    if (!result) return;

    // Map dimension name to score and status
    let score = 0;
    if (dimensionName === 'Relevance' && result.relevance_score !== undefined) {
      score = result.relevance_score;
    } else if (dimensionName === 'Reliability' && result.reliability_score !== undefined) {
      score = result.reliability_score;
    } else {
      score = result.overall_score;
    }

    // Determine compliance status from score
    const status: EducationalMetric['status'] =
      score >= 85 ? 'compliant' :
      score >= 70 ? 'partial' :
      score >= 50 ? 'partial' :
      'non_compliant';

    // Create the metric object
    const metric: EducationalMetric = {
      name: dimensionName,
      score: Math.round(score),
      status,
      description: `${dimensionName} assessment for FDA AI Credibility Framework`,
    };

    setBasisMetric(metric);
    setBasisDrawerOpen(true);
    setBasisLoading(true);
    setBasisError(null);

    try {
      // Fetch the regulatory requirement for this dimension
      const principleKey = dimensionName.toLowerCase();
      const response = await fetch(
        `/api/v1/regulatory/library/requirements/fda_credibility/${principleKey}`
      );

      if (response.ok) {
        const data = await response.json();
        setBasisRequirement(data);
      } else if (response.status === 404) {
        // If specific dimension not found, try to get any FDA credibility requirement
        setBasisRequirement(null);
      } else {
        throw new Error('Failed to fetch regulatory requirement');
      }
    } catch (err) {
      console.error('Error fetching regulatory basis:', err);
      setBasisError('Unable to load regulatory basis information');
    } finally {
      setBasisLoading(false);
    }
  }, [result]);

  const handleCloseBasisDrawer = useCallback(() => {
    setBasisDrawerOpen(false);
    setBasisMetric(null);
    setBasisRequirement(null);
    setBasisError(null);
  }, []);

  // Loading state
  if (loading && !result) {
    return <LoadingState compact={compact} />;
  }

  // Error state
  if (error && !result) {
    return <ErrorState error={error} onRetry={handleRefresh} compact={compact} />;
  }

  // Empty state
  if (!result) {
    return (
      <EmptyState
        onRunAssessment={showActions ? handleRunAssessment : undefined}
        isRunning={isRunning}
        compact={compact}
      />
    );
  }

  // Success state with data
  return (
    <div
      className={`fda-credibility-card ${compact ? 'fda-credibility-card--compact' : ''} ${className}`}
    >
      {/* Header */}
      <div className="fda-credibility-card__header">
        <div className="fda-credibility-card__title-row">
          <h3 className="fda-credibility-card__title">FDA AI Credibility</h3>
          <CredibilityBadge level={result.credibility_level} compact={compact} />
        </div>
        {showActions && (
          <div className="fda-credibility-card__actions">
            <button
              className="fda-credibility-card__action-btn fda-credibility-card__action-btn--refresh"
              onClick={handleRefresh}
              disabled={loading}
              title="Refresh"
              aria-label="Refresh assessment"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
            <button
              className="fda-credibility-card__action-btn fda-credibility-card__action-btn--run"
              onClick={handleRunAssessment}
              disabled={isRunning}
              title="Run Assessment"
              aria-label="Run new assessment"
            >
              {isRunning ? (
                <svg className="fda-spinner" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                  <path d="M12 2a10 10 0 0110 10" strokeLinecap="round" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 3l14 9-14 9V3z" />
                </svg>
              )}
            </button>
            <ExportCredibilityReport
              journeyId={journeyId}
              compact={compact}
              disabled={!result}
            />
          </div>
        )}
      </div>

      {/* Body */}
      <div className="fda-credibility-card__body">
        {/* Score Section */}
        <div className="fda-credibility-card__score-section">
          <div className="fda-credibility-card__gauge">
            <ScoreGauge
              score={result.overall_score}
              size={compact ? 'sm' : 'md'}
              variant="full"
              showValue={true}
              showPercentSign={false}
              animated={true}
            />
          </div>
          <div className="fda-credibility-card__score-info">
            <FitForUseBadge isFitForUse={result.is_fit_for_use} />
            {!compact && (
              <p className="fda-credibility-card__score-desc">
                {getCredibilityLevelDescription(result.credibility_level)}
              </p>
            )}
          </div>
        </div>

        {/* Dimension Scores */}
        {!compact && (result.relevance_score !== undefined || result.reliability_score !== undefined) && (
          <div className="fda-credibility-card__dimensions">
            <DimensionScore
              label="Relevance"
              score={result.relevance_score}
              onViewBasis={() => handleViewBasis('Relevance')}
            />
            <DimensionScore
              label="Reliability"
              score={result.reliability_score}
              onViewBasis={() => handleViewBasis('Reliability')}
            />
          </div>
        )}

        {/* Model Risk Section */}
        {result.model_risk && (
          <ModelRiskDisplay
            influence={result.model_risk.influence}
            consequence={result.model_risk.consequence}
            calculatedRisk={result.model_risk.calculated_risk}
          />
        )}

        {/* Gaps Summary */}
        <GapsSummaryDisplay
          critical={result.gaps_summary.critical}
          major={result.gaps_summary.major}
          minor={result.gaps_summary.minor}
          observation={result.gaps_summary.observation}
          compact={compact}
        />

        {/* View Details Link */}
        {onViewDetails && (
          <button className="fda-credibility-card__view-details" onClick={onViewDetails}>
            View Details
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}
      </div>

      {/* Footer with timestamp */}
      <div className="fda-credibility-card__footer">
        <span className="fda-credibility-card__timestamp">
          Assessed: {new Date(result.assessed_at).toLocaleDateString()}
        </span>
        <span className="fda-credibility-card__version">v{result.assessor_version}</span>
      </div>

      {/* DC-1065: Regulatory Basis Drawer */}
      <RegulatoryBasisDrawer
        isOpen={basisDrawerOpen}
        onClose={handleCloseBasisDrawer}
        metric={basisMetric}
        requirement={basisRequirement}
        loading={basisLoading}
        error={basisError}
      />
    </div>
  );
};

export default FDACredibilityCard;
